import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Megaphone, Check, Loader2, Upload, X, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TOTAL_MB = 300;

/**
 * SponsoredPurchasePage — public self-serve flow for buying a sponsored
 * article slot. 2-step form: (a) fill headline + body + upload media up to
 * 300MB; (b) redirect to Stripe Checkout. Admin price + discount is fetched
 * from /api/public/sponsored-settings.
 */
export default function SponsoredPurchasePage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ email: '', company: '', phone: '', headline: '', body: '' });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountToken, setDiscountToken] = useState('');
  const [discount, setDiscount] = useState(null);
  const [validating, setValidating] = useState(false);

  async function applyCode() {
    if (!discountCode.trim()) { setDiscount(null); return; }
    if (!settings) return;
    setValidating(true);
    try {
      const subtotal = parseFloat(settings.effective_price_eur ?? settings.price_eur ?? 0);
      const r = await fetch(`${API}/public/marketing-discount/validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode, subtotal_eur: subtotal, scope: 'sponsored' }),
      });
      const d = await r.json();
      if (!r.ok) { setDiscount(null); toast.error(d?.detail || 'Nevažeći kod'); }
      else { setDiscount(d); toast.success(d.free ? 'Kod primijenjen — narudžba je BESPLATNA!' : `Kod primijenjen: −€${d.discount_eur.toFixed(2)}`); }
    } catch { setDiscount(null); toast.error('Nevažeći kod'); }
    finally { setValidating(false); }
  }

  useEffect(() => {
    fetch(`${API}/public/sponsored-settings`).then(r => r.ok ? r.json() : null).then(setSettings).catch(() => {});
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('paid') === '1') setSuccess(true);
    // Prefill from /popust/:token landing
    const d = qs.get('d');
    const tok = qs.get('t');
    if (d) setDiscountCode(d.toUpperCase());
    if (tok) setDiscountToken(tok);
  }, []);

  // Auto-validate once settings (and prefilled code) are available
  useEffect(() => {
    if (settings && discountCode && !discount && !validating) {
      applyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const totalSize = files.reduce((s, f) => s + f.size, 0);
  const totalMb = (totalSize / (1024 * 1024));
  const overLimit = totalMb > MAX_TOTAL_MB;

  function handleFiles(newFiles) {
    const combined = [...files, ...Array.from(newFiles)];
    const size = combined.reduce((s, f) => s + f.size, 0);
    if (size > MAX_TOTAL_MB * 1024 * 1024) {
      toast.error(`Premaši 300MB (${(size / 1024 / 1024).toFixed(1)}MB)`);
      return;
    }
    setFiles(combined);
  }

  function removeFile(idx) { setFiles(f => f.filter((_, i) => i !== idx)); }

  async function submit(e) {
    e.preventDefault();
    if (!form.email || !form.headline) { toast.error('E-mail i naslov su obavezni'); return; }
    if (overLimit) { toast.error('Ukupna veličina datoteka prelazi 300MB'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('email', form.email);
      fd.append('company', form.company || '');
      fd.append('phone', form.phone || '');
      fd.append('headline', form.headline);
      fd.append('body', form.body || '');
      files.forEach(f => fd.append('files', f));
      const r = await fetch(`${API}/public/sponsored/submit`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) { toast.error(d?.detail || 'Greška pri slanju'); setSubmitting(false); return; }
      // Now create Stripe checkout
      const checkoutRes = await fetch(`${API}/public/sponsored/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: d.id,
          email: form.email,
          discount_code: discount?.code || null,
          discount_link_token: discountToken || null,
          success_url: `${window.location.origin}/sponzorirani-clanak?paid=1`,
          cancel_url: `${window.location.origin}/sponzorirani-clanak?cancelled=1`,
        }),
      });
      const cd = await checkoutRes.json();
      if (cd.url) window.location.href = cd.url;
      else toast.error(cd?.detail || 'Greška pri kreiranju plaćanja');
    } catch (ex) { toast.error(String(ex?.message || ex)); }
    finally { setSubmitting(false); }
  }

  if (!settings) return <div className="min-h-screen bg-gray-950"><div className="max-w-3xl mx-auto p-10 text-gray-400"><Loader2 className="animate-spin" /></div></div>;

  const effPrice = settings.effective_price_eur ?? settings.price_eur;
  const tierDiscount = parseFloat(settings.discount_pct || 0);

  const pageTitle = `${settings?.title || t('sponsored.widget_title')} — e-mina.eu`;

  return (
    <>
      <Helmet><title>{pageTitle}</title></Helmet>
      <div className="min-h-screen bg-gray-950 text-white" data-testid="sponsored-page">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="flex items-center gap-3 mb-6">
            <Megaphone className="text-red-500" size={28} />
            <div>
              <h1 className="text-3xl font-bold">{settings.title || t('sponsored.widget_title')}</h1>
              <p className="text-gray-400 text-sm mt-1">{settings.subtitle || t('sponsored.widget_subtitle')}</p>
            </div>
          </div>

          {success ? (
            <div className="bg-emerald-900/20 border border-emerald-700 rounded-xl p-8 text-center" data-testid="sp-success">
              <div className="w-14 h-14 rounded-full bg-emerald-600 mx-auto flex items-center justify-center mb-3"><Check size={28} /></div>
              <p className="text-lg font-bold mb-1">{t('sponsored.success')}</p>
              <p className="text-sm text-emerald-300">Uskoro ćemo vas kontaktirati radi finalne objave članka.</p>
            </div>
          ) : (
            <>
              {/* Price / benefits card */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-900/60 border border-red-500/30 rounded-2xl p-6 mb-6 relative" data-testid="sp-price-card">
                {tierDiscount > 0 && settings.badge_label && (
                  <span className="absolute -top-3 -right-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg" data-testid="sp-badge">
                    {settings.badge_label} -{Math.round(tierDiscount)}%
                  </span>
                )}
                <div className="flex items-end gap-2 mb-4">
                  {tierDiscount > 0 && (
                    <span className="text-lg text-gray-500 line-through">€ {Number(settings.price_eur).toFixed(2)}</span>
                  )}
                  <p className="text-4xl font-bold text-emerald-400" data-testid="sp-eff-price">€ {Number(effPrice).toFixed(2)}</p>
                  <span className="text-xs text-gray-400 mb-1">po članku</span>
                </div>
                <ul className="space-y-2 text-sm text-gray-200">
                  {(settings.bullets || []).map((b, i) => (
                    <li key={i} className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> {b}</li>
                  ))}
                </ul>
              </div>

              {/* Form */}
              <form onSubmit={submit} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-4" data-testid="sp-form">
                <h2 className="text-lg font-bold mb-1">{t('sponsored.form_title')}</h2>

                <div className="grid md:grid-cols-3 gap-3">
                  <input required type="email" placeholder="E-mail" value={form.email}
                         onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                         className="bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm" data-testid="sp-email" />
                  <input type="text" placeholder="Tvrtka (opcionalno)" value={form.company}
                         onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                         className="bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm" />
                  <input type="text" placeholder="Telefon (opcionalno)" value={form.phone}
                         onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                         className="bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm" />
                </div>

                <input required type="text" placeholder={t('sponsored.headline')} value={form.headline}
                       onChange={e => setForm(f => ({ ...f, headline: e.target.value }))}
                       className="w-full bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm font-semibold" data-testid="sp-headline" />
                <textarea placeholder={t('sponsored.body')} rows={8} value={form.body}
                          onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                          className="w-full bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm" data-testid="sp-body" />

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold mb-2"><Upload size={14} /> {t('sponsored.media')}</label>
                  <input type="file" multiple accept="image/*,video/*" onChange={e => handleFiles(e.target.files)} className="block text-sm" data-testid="sp-files" />
                  <p className="text-[11px] text-gray-500 mt-1">{t('sponsored.media_hint')}</p>
                  <p className={`text-xs mt-1 ${overLimit ? 'text-red-400' : 'text-gray-400'}`}>
                    Ukupno: {totalMb.toFixed(1)} / {MAX_TOTAL_MB} MB {overLimit && '(prelazi limit!)'}
                  </p>
                  {files.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {files.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs bg-gray-950 px-2 py-1 rounded" data-testid={`sp-file-${i}`}>
                          <span className="flex-1 truncate">{f.name}</span>
                          <span className="text-gray-500">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                          <button type="button" onClick={() => removeFile(i)} className="text-red-400 hover:text-red-300"><X size={12} /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex gap-2 pt-1">
                  <input placeholder="Promo kod (opcionalno)" value={discountCode}
                         onChange={e => setDiscountCode(e.target.value.toUpperCase())}
                         className="flex-1 bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm font-mono"
                         data-testid="sp-discount-code" />
                  <button type="button" onClick={applyCode} disabled={validating || !discountCode}
                          className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded disabled:opacity-50"
                          data-testid="sp-apply-code">
                    {validating ? <Loader2 size={12} className="animate-spin" /> : 'Primijeni'}
                  </button>
                </div>
                {discount && (
                  <div className="text-xs text-emerald-400 -mt-1" data-testid="sp-discount-applied">
                    Kod {discount.code}: {discount.free ? '−100% (FREE)' : `−€ ${discount.discount_eur.toFixed(2)}`}
                  </div>
                )}
                {discountToken && (
                  <div className="text-[11px] text-emerald-400 -mt-1" data-testid="sp-one-time-link-badge">
                    Jednokratni link je aktivan — popust će biti primijenjen na ovu kupnju.
                  </div>
                )}

                <button type="submit" disabled={submitting || overLimit}
                        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded flex items-center justify-center gap-2 disabled:opacity-50" data-testid="sp-submit">
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                  {discount?.free ? 'Završi narudžbu (BESPLATNO)' : `${t('sponsored.submit')} (€ ${(discount ? Math.max(0, Number(effPrice) - discount.discount_eur) : Number(effPrice)).toFixed(2)})`}
                </button>
                <p className="text-[11px] text-center text-gray-500">Sigurno plaćanje: Stripe · VISA · Mastercard · Apple Pay · Google Pay</p>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}
